<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-gray-900 bg-opacity-50 backdrop-blur-sm p-4">
    <div class="relative w-full max-w-4xl rounded-lg bg-white dark:bg-gray-800 shadow-xl">
      <!-- Header -->
      <div class="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 p-4">
        <h3 class="text-xl font-semibold text-gray-900 dark:text-white">Upgrade Your Plan</h3>
        <button @click="close" class="text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
          <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
        </button>
      </div>

      <!-- Body -->
      <div class="p-6">
        <div class="grid gap-6 md:grid-cols-3">
            <!-- BASIC -->
            <div class="rounded-lg border border-gray-200 dark:border-gray-700 p-6 flex flex-col bg-white dark:bg-gray-800" :class="{ 'ring-2 ring-primary-500': currentTier === 'BASIC' }">
                <h4 class="text-lg font-bold text-gray-900 dark:text-white">Basic</h4>
                <p class="text-3xl font-bold mt-2 text-gray-900 dark:text-white">$0<span class="text-sm font-normal text-gray-500 dark:text-gray-400">/mo</span></p>
                <ul class="mt-4 space-y-2 text-sm text-gray-600 dark:text-gray-300 flex-1">
                    <li>1 Portfolio</li>
                    <li>50 Transactions</li>
                    <li>1 personal asset</li>
                    <li>1 Liability</li>
                </ul>
                <button disabled class="mt-6 w-full rounded bg-gray-100 dark:bg-gray-700 py-2 text-gray-500 dark:text-gray-400 cursor-not-allowed">
                    {{ currentTier === 'BASIC' ? 'Current Plan' : 'Free' }}
                </button>
            </div>

            <!-- PRO -->
            <div class="rounded-lg border border-gray-200 dark:border-gray-700 p-6 flex flex-col relative bg-white dark:bg-gray-800" :class="{ 'ring-2 ring-primary-500': currentTier === 'PRO' }">
                <h4 class="text-lg font-bold text-primary-600 dark:text-primary-400">Pro</h4>
                <p class="text-3xl font-bold mt-2 text-gray-900 dark:text-white">$4.99<span class="text-sm font-normal text-gray-500 dark:text-gray-400">/mo</span></p>
                <ul class="mt-4 space-y-2 text-sm text-gray-600 dark:text-gray-300 flex-1">
                    <li>10 Portfolios</li>
                    <li>2,000 Transactions</li>
                    <li>Unlimited personal assets</li>
                    <li>10 Liabilities</li>
                </ul>
                <button 
                    v-if="currentTier === 'PRO'"
                    disabled
                    class="mt-6 w-full rounded bg-gray-100 dark:bg-gray-700 py-2 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                >
                    Current Plan
                </button>
                 <button 
                    v-else
                    @click="upgrade('PRO')"
                    :disabled="loading === 'PRO'"
                    class="mt-6 w-full rounded bg-primary-600 py-2 text-white hover:bg-primary-700 dark:hover:bg-primary-500 transition-colors"
                >
                    {{ loading === 'PRO' ? 'Processing...' : 'Upgrade to Pro' }}
                </button>
            </div>

            <!-- TRADER -->
            <div class="rounded-lg border border-gray-200 dark:border-gray-700 p-6 flex flex-col bg-white dark:bg-gray-800" :class="{ 'ring-2 ring-primary-500': currentTier === 'TRADER' }">
                <h4 class="text-lg font-bold text-primary-700 dark:text-primary-400">Wealth</h4>
                <p class="text-3xl font-bold mt-2 text-gray-900 dark:text-white">$12.99<span class="text-sm font-normal text-gray-500 dark:text-gray-400">/mo</span></p>
                <ul class="mt-4 space-y-2 text-sm text-gray-600 dark:text-gray-300 flex-1">
                    <li>Unlimited Portfolios</li>
                    <li>Unlimited Transactions</li>
                    <li>Unlimited Liabilities</li>
                    <li>Priority Feature Requests</li>
                </ul>
                <button 
                    v-if="currentTier === 'TRADER'"
                    disabled
                    class="mt-6 w-full rounded bg-gray-100 dark:bg-gray-700 py-2 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                >
                    Current Plan
                </button>
                 <button 
                    v-else
                    @click="upgrade('TRADER')"
                    :disabled="loading === 'TRADER'"
                    class="mt-6 w-full rounded bg-primary-700 py-2 text-white hover:bg-primary-600 dark:hover:bg-primary-500 transition-colors"
                >
                     {{ loading === 'TRADER' ? 'Processing...' : 'Upgrade to Wealth' }}
                </button>
            </div>
        </div>
        
        <div class="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
           {{ securityMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useSubscription } from '@/composables/useSubscription';
import { usePayment } from '@/composables/usePayment';

export default {
  name: 'UpgradePlanModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const store = useStore();
    const { createCheckout } = useSubscription();
    const { getSecurityMessage } = usePayment();
    
    const loading = ref(null); // 'PRO' | 'TRADER' | null
    
    const currentTier = computed(() => store.getters['auth/subscription']?.tier || 'BASIC');
    const securityMessage = computed(() => getSecurityMessage());

    const close = () => {
      emit('close');
    };

    const upgrade = async (plan) => {
      loading.value = plan;
      try {
        await createCheckout(plan.toLowerCase());
      } catch (e) {
        console.error(e);
        const msg = e.response?.data?.message || e.message;
        store.dispatch('ui/showToast', {
          message: 'Failed to initiate checkout: ' + msg,
          type: 'error'
        });
      } finally {
        loading.value = null;
      }
    };

    return {
        close,
        upgrade,
        currentTier,
        loading,
        securityMessage
    };
  }
};
</script>
